import {
  Box,
  Card,
  CardHeader,
  Skeleton,
} from '@mui/material';
import {
  FC, useEffect, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import Chart, { useChart } from '@mgm/common/components/charts';
import { TMember } from '../../../../types';
import { useAppSelector } from '../../../../hooks/useAppStore';
import {
  getDailyStateSelectedLocation,
  getEmployeeRates, getIsOrdersLoading,
} from '../../../../store/orders/selectors';
import { getCurrencyByCode, getPlatformData } from '../../../../store/common/selectors';
import { mergeEmployeeRates } from '../../../../utils/helpers/orders.helper';
import { AnimatedDivider } from '../../../../components';

type TEmployeesRates = {
  members: Array<TMember>
};

const EmployeesRates: FC<TEmployeesRates> = ({ members }) => {
  const dailyStateSelectedLocation = useAppSelector(getDailyStateSelectedLocation);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const { t } = useTranslation(['commissions', 'dashboard', 'orders', 'common']);
  const { locations } = useAppSelector(getPlatformData);
  const chartRef = useRef<any>(null);
  const isChartMounted = useRef(false);
  const employeeRates = useAppSelector(getEmployeeRates);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const salesAndCommissionData = useMemo(() => {
    if (dailyStateSelectedLocation === 0) {
      return mergeEmployeeRates(employeeRates);
    }
    return employeeRates[dailyStateSelectedLocation] || { salesByEmployee: {}, commissionByEmployee: {} };
  }, [dailyStateSelectedLocation, employeeRates]);

  const salesData = members.map((member) => salesAndCommissionData.salesByEmployee[member.email] || 0.0);
  const commissionData = members.map((member) => salesAndCommissionData.commissionByEmployee[member.email] || 0.0);

  const categories = useMemo(() => members.map((member) => {
    const locationsWithSales = new Set<string>();

    Object.entries(employeeRates ?? {}).forEach(([locationId, { salesByEmployee }]) => {
      if (salesByEmployee[member.email]) {
        const locationName = locations.find((loc) => loc.id === Number(locationId))?.name || 'unassigned';
        locationsWithSales.add(locationName);
      }
    });

    const locationsString = Array.from(locationsWithSales).join(', ');
    return locationsWithSales.size > 0 ? `${member.name} (${locationsString})` : member.name;
  }), [members, employeeRates, locations]);

  const chartOptions = useChart({
    chart: {
      events: {
        mounted: (chart) => {
          isChartMounted.current = true;
          chartRef.current = chart;
        },
      },
    },
    colors: ['rgb(67, 26, 158)', 'rgba(255, 86, 48, 0.48)'],
    tooltip: {
      enabled: true,
      custom({ dataPointIndex }) {
        const member = categories[dataPointIndex] || '';
        const sales = salesData[dataPointIndex] || 0;
        const commission = commissionData[dataPointIndex] || 0;
        return `
          <div style='padding: 8px'>
            ${member ? `<strong>${member}</strong><br />` : ''}
            <strong>${t('sales', { ns: 'dashboard' })}:</strong> ${sales.toFixed(2)} ${getCurrency().mark}<br />
            <strong>${t('commissions')}:</strong> ${commission.toFixed(2)} ${getCurrency().mark}
          </div>`;
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '40%',
        borderRadius: 2,
      },
    },
    xaxis: {
      categories,
      labels: {
        formatter(value) {
          return value.length > 10 ? `${value.substring(0, 10)}...` : value;
        },
        tooltip: {
          enabled: true,
          offsetY: 10,
        },
      },
    } as ApexXAxis,
  });

  useEffect(() => {
    if (isChartMounted.current && chartRef.current?.updateOptions) {
      try {
        chartRef.current.updateOptions({
          tooltip: {
            enabled: true,
            custom({ dataPointIndex }: any) {
              const member = categories[dataPointIndex] || '';
              const sales = salesData[dataPointIndex] || 0;
              const commission = commissionData[dataPointIndex] || 0;
              return `
                <div style='padding: 8px'>
                  ${member ? `<strong>${member}</strong><br />` : ''}
                  <strong>${t('sales', { ns: 'dashboard' })}:</strong> ${sales.toFixed(2)} ${getCurrency().mark}<br />
                  <strong>${t('commissions')}:</strong> ${commission.toFixed(2)} ${getCurrency().mark}
                </div>`;
            },
          },
          xaxis: {
            categories,
          },
        }, true, true);
      } catch (error) {
        console.error('Failed to update chart options:', error);
      }
    }
  }, [categories]);

  const validSeries = [
    { name: t('sales', { ns: 'dashboard' }), data: salesData.length ? salesData : [0] },
    { name: t('commissions'), data: commissionData.length ? commissionData : [0] },
  ];

  return (
    <Card>
      <CardHeader
        sx={{
          p: 2,
        }}
        title={t('employeesStats', { ns: 'orders' })}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mx: 3 }} dir="ltr">
        {isOrdersLoading ? (
          <Box py={3}>
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : (
          <Chart
            type="bar"
            series={validSeries}
            options={chartOptions}
            height={430}
          />
        )}
      </Box>
    </Card>
  );
};

export { EmployeesRates };
